import ApolloClient from 'apollo-client'
import { NormalizedCacheObject } from 'apollo-cache-inmemory'
import { GraphQLError } from 'graphql'

import { ErrorHandler } from '@sketch/tracing'

import { getActiveAuthorization } from '@sketch/modules-common'
import { refreshToken, checkIfTokenIsExpired } from './refreshToken'
import { addCredentialsToOptions } from './utils'

type FetchType = typeof window.fetch

const UNAUTHORIZED_MESSAGES = [
  // GraphQL error message.
  'The access token expired',
  // Lambda error message
  'Invalid token',
]

const EXPIRE_TOKEN_REASONS = ['TOKEN_EXPIRED', 'ACCESS_TOKEN_EXPIRED']

const createOAuthFetcher = (
  getClient: () => ApolloClient<NormalizedCacheObject>
): FetchType => async (uri, options) => {
  let authorization = getActiveAuthorization(getClient().cache)

  if (authorization) {
    const isTokenExpired = checkIfTokenIsExpired(
      authorization.fragment.expirationDateTime ?? '0'
    )

    if (isTokenExpired) {
      await refreshToken(getClient(), authorization)
      authorization = getActiveAuthorization(getClient().cache)
    }
  }

  const safeGuardAuthorization = authorization ? [authorization] : []
  const response = await fetch(
    uri,
    addCredentialsToOptions(safeGuardAuthorization, options)
  )

  // If it doesn't have credentials then it makes no sense continuing
  // because we will validate if the request had a valid token or needs refreshing
  // no credentials equal no additional check
  if (!authorization) {
    return response
  }

  // In order to return the initial response we need to clone it before we use it
  const cloneResponse = response.clone()

  const jsonResponse = await response.json().catch(() => ({}))
  const errorsInResponse: GraphQLError[] = jsonResponse?.errors ?? []

  /**
   * When we receive a network error (e.g. 500 Internal Server Error), the body
   * does not follow SketchQL's schema. In those cases, we want to log the error
   * and return as soon as possible.
   *
   * See https://github.com/sketch-hq/Cloud/issues/6687
   */
  if (response.status === 500 || !Array.isArray(errorsInResponse)) {
    ErrorHandler.shouldNeverHappen(
      `Field "errors" is not an array: ${JSON.stringify(errorsInResponse)}`
    )
    return cloneResponse
  }

  const hasError = errorsInResponse
    .map(error => error.extensions?.['reason'])
    .find(reason => EXPIRE_TOKEN_REASONS.includes(reason))
  const hasLegacyError = UNAUTHORIZED_MESSAGES.includes(jsonResponse.message)

  if (hasError || hasLegacyError) {
    const actualCredentials = getActiveAuthorization(getClient().cache)

    // We are just validating if the credentials have been updated in the meanwhile
    // from the moment the request was made until 401, because if they were we are going
    // to refresh an already refreshed token and BE will return 401 forcing a user sign-out
    // not a expected behaviour
    if (
      authorization.fragment.authToken !== actualCredentials?.fragment.authToken
    ) {
      const safeGuardAuthorization = actualCredentials
        ? [actualCredentials]
        : []

      return fetch(
        uri,
        addCredentialsToOptions(safeGuardAuthorization, options)
      )
    }

    const wasSuccessful =
      actualCredentials && (await refreshToken(getClient(), actualCredentials))

    if (!wasSuccessful) {
      // If there's an issue refreshing the token we should return the original error payload
      // because it can be more specific than the refresh token one
      return cloneResponse
    }

    return fetch(uri, addCredentialsToOptions([], options))
  }

  return cloneResponse
}

export { createOAuthFetcher }
